import React, { useEffect, useState } from "react";
import { Plus, Truck } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
	Delivery,
	DeliveryStatusEnum,
	CreateDeliveryParams,
} from "@/lib/types/delivery.types";
import { DeliveryCard } from "@/dentlab/src/pages/job-page/job-page-components/deliveries/delivery-card.component";
import { DeliveryDetailDialog } from "@/dentlab/src/pages/job-page/job-page-components/deliveries/delivery-detail-dialog.component";
import { CreateDeliveryDialog } from "@/dentlab/src/pages/job-page/job-page-components/deliveries/create-delivery-dialog.component";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import {
	Collapsible,
	CollapsibleTrigger,
	CollapsibleContent,
} from "@/dentlab/src/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface JobSheetDeliveriesProps {
	jobId: number;
}

export const JobSheetDeliveries: React.FC<JobSheetDeliveriesProps> = ({
	jobId,
}) => {
	const [deliveries, setDeliveries] = useState<Delivery[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedDeliveryId, setSelectedDeliveryId] = useState<number | null>(
		null
	);
	const [createOpen, setCreateOpen] = useState(false);
	const [isOpen, setIsOpen] = useState(true);

	const fetchDeliveries = async () => {
		const { data, error } = await supabase
			.from("deliveries")
			.select("*")
			.eq("job_id", jobId)
			.order("created_at", { ascending: false });
		if (error) {
			Logger.error(error, {}, "Error fetching deliveries in sheet");
		}
		setDeliveries((data as Delivery[]) ?? []);
		setLoading(false);
	};

	useEffect(() => {
		fetchDeliveries();
	}, [jobId]);

	const selectedDelivery =
		selectedDeliveryId !== null
			? deliveries.find((d) => d.id === selectedDeliveryId) ?? null
			: null;

	const handleCreate = async (params: CreateDeliveryParams) => {
		const { error } = await supabase.rpc("create_delivery", {
			p_job_id: params.job_id,
			p_courier_type: params.courier_type,
			p_profile_id: params.profile_id ?? undefined,
			p_courier_company_name: params.courier_company_name ?? undefined,
			p_description: params.description ?? undefined,
			p_scheduled_departure_at:
				params.scheduled_departure_at ?? undefined,
			p_is_final_delivery: params.is_final_delivery ?? false,
		});
		if (error) {
			Logger.error(error, {}, "Error creating delivery from sheet");
			showNotification({
				message: "Fehler beim Erstellen",
				type: "error",
			});
			return;
		}
		showNotification({ message: "Lieferung erstellt", type: "success" });
		await fetchDeliveries();
	};

	const handleStatusChange = async (
		deliveryId: number,
		status: DeliveryStatusEnum
	) => {
		const { error } = await supabase.rpc("update_delivery_status", {
			p_delivery_id: deliveryId,
			p_status: status,
		});
		if (error) {
			Logger.error(
				error,
				{},
				"Error updating delivery status from sheet"
			);
			return;
		}
		setDeliveries((prev) =>
			prev.map((d) =>
				d.id === deliveryId
					? {
							...d,
							status,
							delivered_at:
								status === DeliveryStatusEnum.DELIVERED
									? new Date().toISOString()
									: d.delivered_at,
						}
					: d
			)
		);
	};

	const handleUpdate = async (
		deliveryId: number,
		fields: Partial<Delivery>
	) => {
		const { error } = await supabase
			.from("deliveries")
			.update(fields)
			.eq("id", deliveryId);
		if (error) {
			Logger.error(error, {}, "Error updating delivery from sheet");
			return;
		}
		setDeliveries((prev) =>
			prev.map((d) => (d.id === deliveryId ? { ...d, ...fields } : d))
		);
	};

	const handleDelete = async (deliveryId: number) => {
		const { error } = await supabase
			.from("deliveries")
			.delete()
			.eq("id", deliveryId);
		if (error) {
			Logger.error(error, {}, "Error deleting delivery from sheet");
			return;
		}
		setDeliveries((prev) => prev.filter((d) => d.id !== deliveryId));
	};

	const activeCount = deliveries.filter(
		(d) =>
			d.status === DeliveryStatusEnum.PENDING ||
			d.status === DeliveryStatusEnum.IN_TRANSIT
	).length;

	return (
		<>
			<Collapsible
				open={isOpen}
				onOpenChange={setIsOpen}
				className="mb-4"
			>
				<div className="flex items-center justify-between">
					<CollapsibleTrigger className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
						<ChevronDown
							className={`h-4 w-4 transition-transform ${
								isOpen ? "" : "-rotate-90"
							}`}
						/>
						<Truck className="h-4 w-4" />
						Lieferungen
						{deliveries.length > 0 && (
							<span className="text-xs text-gray-400 font-normal">
								({deliveries.length})
							</span>
						)}
					</CollapsibleTrigger>
					<button
						onClick={() => setCreateOpen(true)}
						className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50 transition-colors"
						data-testid="sheet-delivery-create-button"
						aria-label="Neue Lieferung erstellen"
					>
						<Plus className="h-3 w-3" />
						Neu
					</button>
				</div>
				<CollapsibleContent>
					{loading ? (
						<p className="text-xs text-gray-400 py-2">Laden...</p>
					) : deliveries.length === 0 ? (
						<p className="text-xs text-gray-400 py-2">
							Keine Lieferungen
						</p>
					) : (
						<div className="space-y-2 pt-1">
							{deliveries.map((d) => (
								<DeliveryCard
									key={d.id}
									delivery={d}
									onStatusChange={handleStatusChange}
									onDelete={handleDelete}
									onClick={(del) =>
										setSelectedDeliveryId(del.id)
									}
								/>
							))}
						</div>
					)}
				</CollapsibleContent>
			</Collapsible>

			<CreateDeliveryDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				jobId={jobId}
				onSubmit={handleCreate}
			/>

			<DeliveryDetailDialog
				delivery={selectedDelivery}
				open={!!selectedDelivery}
				onOpenChange={(open) => {
					if (!open) setSelectedDeliveryId(null);
				}}
				onStatusChange={handleStatusChange}
				onUpdate={handleUpdate}
				onFilesChanged={fetchDeliveries}
			/>
		</>
	);
};
